import React, { FC, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment, PricingBlockBespokeQueryQuery } from "@graphqlTypes"

import { addThousandCommas, calculateMonthlyPriceWithAnnualDiscount, generateConditionalClassName } from "@utils"

import Button, { BUTTON_VARIANTS } from "@components/Button/Button"
import TextLink, { TEXT_LINK_SIZES } from "@components/TextLink/TextLink"

import { PricingBlockSECTION, PricingPlanCardDIV } from "./PricingBlock.styles"

type PricingBlockJsonPayload = {
  title: string
  description: string
  ctaText: string
  ctaUrl: string
}

const PricingBlock: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const { pricingBlockJson } = useStaticQuery<PricingBlockBespokeQueryQuery>(graphql`
    query PricingBlockBespokeQuery {
      pricingBlockJson: contentfulJsonData(contentful_id: {eq: "45c4tePUA3uIu5WPPfUCez"}) {
        json {
          annualDiscountPercentage
          plans {
            id
            ctaText
            ctaLink
            name
            baseContacts
            maxContacts
            prices {
              _0
            }
          }
        }
      }
    }
  `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as PricingBlockJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: PricingBlock (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (
      !json ||
      !(
        pricingBlockJson?.json?.plans &&
        pricingBlockJson.json?.annualDiscountPercentage
      )
    ) return null

    const { annualDiscountPercentage, plans } = pricingBlockJson.json

    return (
      <PricingBlockSECTION className="PricingBlock margin-bottom--XL">
        <div className="container">
          <div className="grid">
            <div className="section-content column column--2-12-S column--3-11-M column--1-5-L">
              <h2 dangerouslySetInnerHTML={{ __html: json.title }} />
              <p className="type--text-L margin-bottom--M" dangerouslySetInnerHTML={{ __html: json.description }} />
              {json.ctaText && <TextLink href={json.ctaUrl} size={TEXT_LINK_SIZES.L}>{json.ctaText}</TextLink>}
            </div>
            <div className="pricing-cards-wrapper column column--2-12-M column--5-13-L">
              <div className="pricing-cards">
                {plans.map(plan => (!plan || plan.id === "pro") ? null : (
                  <PricingPlanCardDIV
                    className={generateConditionalClassName({
                      "plan": true,
                      [`plan--${plan.id}`]: true,
                    })}
                    key={plan.id}
                  >
                    <div className="gradient-background" />
                    <div className="plan-details">
                      <h4 className="type--headline-medium-M margin-bottom--base-1-5">{plan.name}</h4>
                      <div className="plan-price margin-bottom--base-1-5">
                        <span
                          className="plan-price--value type--headline-medium-5XL font-weight--medium color--text-headline"
                        >
                          {/* Annual discount included: */}
                          ${calculateMonthlyPriceWithAnnualDiscount(plan.prices?._0 || 0, annualDiscountPercentage)}
                        </span>
                        {plan.id !== "free" && (
                          <span className="plan-price--mo type--text-XS color--text-default">/month</span>
                        )}
                      </div>
                      <p className="plan-description type--text-S color--text-headline margin-bottom--0">
                        {plan.id === "free" ? (
                          <strong>Up to {addThousandCommas(plan.maxContacts || 0)} contacts</strong>
                        ) : (
                          <strong>Starts with {addThousandCommas(plan.baseContacts || 0)} contacts</strong>
                        )}
                        <br />
                        and unlimited users
                      </p>
                    </div>
                    <Button
                      href={plan.ctaLink || "/"}
                      variant={plan.id === "free" ? BUTTON_VARIANTS.SECONDARY : undefined}
                    >
                      {plan.ctaText || ""}
                    </Button>
                  </PricingPlanCardDIV>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PricingBlockSECTION>
    )
  }

export default PricingBlock
